import React, { useState, useEffect } from 'react';
import { Wifi, WifiOff, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useOfflineStorage } from '@/hooks/useOfflineData';

interface OfflineIndicatorProps {
  className?: string;
  showDetails?: boolean;
}

export const OfflineIndicator: React.FC<OfflineIndicatorProps> = ({ 
  className,
  showDetails = false 
}) => {
  const { isOnline, getOfflineActions, syncOfflineActions } = useOfflineStorage();
  const [pendingActions, setPendingActions] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncAttempt, setLastSyncAttempt] = useState<Date | null>(null);

  useEffect(() => {
    const updatePendingActions = () => {
      const actions = getOfflineActions();
      setPendingActions(actions.length);
    };

    updatePendingActions();
    const interval = setInterval(updatePendingActions, 5000);

    return () => clearInterval(interval);
  }, [getOfflineActions]);

  const handleSync = async () => {
    if (!isOnline || isSyncing) return;
    
    setIsSyncing(true);
    setLastSyncAttempt(new Date());
    
    try {
      await syncOfflineActions();
      setPendingActions(0);
    } catch (error) {
      console.error('Erro na sincronização:', error);
    } finally {
      setIsSyncing(false);
    }
  };

  if (isOnline && pendingActions === 0 && !showDetails) {
    return null;
  }

  return (
    <div className={cn(
      "flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300",
      isOnline 
        ? pendingActions > 0 
          ? "bg-yellow-50 text-yellow-700 border border-yellow-200" 
          : "bg-green-50 text-green-700 border border-green-200"
        : "bg-red-50 text-red-700 border border-red-200",
      className
    )}>
      {/* Ícone de status */}
      <div className="flex items-center gap-1">
        {isOnline ? (
          <Wifi className="w-4 h-4" />
        ) : (
          <WifiOff className="w-4 h-4" />
        )}
        
        {isSyncing && (
          <RefreshCw className="w-3 h-3 animate-spin" />
        )}
      </div>

      {/* Status text */}
      <span className="flex-1">
        {!isOnline ? (
          "Offline"
        ) : pendingActions > 0 ? (
          `${pendingActions} ação${pendingActions > 1 ? 'ões' : ''} pendente${pendingActions > 1 ? 's' : ''}`
        ) : (
          "Online"
        )}
      </span>

      {/* Botão de sincronização */}
      {isOnline && pendingActions > 0 && (
        <button
          onClick={handleSync}
          disabled={isSyncing}
          className={cn(
            "flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors",
            "hover:bg-yellow-100 active:bg-yellow-200 disabled:opacity-50"
          )}
        >
          {isSyncing ? (
            <RefreshCw className="w-3 h-3 animate-spin" />
          ) : (
            <RefreshCw className="w-3 h-3" />
          )}
          Sincronizar
        </button>
      )}

      {/* Detalhes expandidos */}
      {showDetails && (
        <div className="ml-2 text-xs opacity-75">
          {lastSyncAttempt && (
            <span>
              Última sync: {lastSyncAttempt.toLocaleTimeString()}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// Componente de banner offline para tela cheia
export const OfflineBanner: React.FC = () => {
  const { isOnline, getOfflineActions } = useOfflineStorage();
  const [pendingActions, setPendingActions] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const updatePendingActions = () => {
      const actions = getOfflineActions();
      setPendingActions(actions.length);
    };

    updatePendingActions();
    const interval = setInterval(updatePendingActions, 5000);

    return () => clearInterval(interval);
  }, [getOfflineActions]);

  useEffect(() => {
    if (!isOnline) {
      setIsVisible(true);
    } else {
      // Delay para mostrar que voltou online
      const timer = setTimeout(() => setIsVisible(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [isOnline]);

  if (!isVisible) return null;

  return (
    <div className={cn(
      "fixed top-0 left-0 right-0 z-50 px-4 py-3 text-center text-sm font-medium transition-all duration-300",
      "transform translate-y-0",
      isOnline 
        ? "bg-green-500 text-white" 
        : "bg-red-500 text-white"
    )}>
      <div className="flex items-center justify-center gap-2">
        {isOnline ? (
          <>
            <CheckCircle className="w-4 h-4" />
            <span>Conexão restaurada</span>
            {pendingActions > 0 && (
              <span className="opacity-90">
                • {pendingActions} ação{pendingActions > 1 ? 'ões' : ''} será{pendingActions > 1 ? 'ão' : ''} sincronizada{pendingActions > 1 ? 's' : ''}
              </span>
            )}
          </>
        ) : (
          <>
            <AlertCircle className="w-4 h-4" />
            <span>Você está offline</span>
            {pendingActions > 0 && (
              <span className="opacity-90">
                • {pendingActions} ação{pendingActions > 1 ? 'ões' : ''} pendente{pendingActions > 1 ? 's' : ''}
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Hook para usar em qualquer componente
export const useOfflineIndicator = () => {
  const { isOnline, getOfflineActions } = useOfflineStorage();
  const [pendingActions, setPendingActions] = useState(0);

  useEffect(() => {
    const updatePendingActions = () => {
      const actions = getOfflineActions();
      setPendingActions(actions.length);
    };

    updatePendingActions();
    const interval = setInterval(updatePendingActions, 5000);

    return () => clearInterval(interval);
  }, [getOfflineActions]);

  return {
    isOnline,
    pendingActions,
    hasOfflineData: pendingActions > 0
  };
};

// Componente de status compacto para header
export const CompactOfflineStatus: React.FC<{ className?: string }> = ({ className }) => {
  const { isOnline, pendingActions } = useOfflineIndicator();

  return (
    <div className={cn(
      "flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium",
      isOnline 
        ? pendingActions > 0
          ? "bg-yellow-100 text-yellow-700"
          : "bg-green-100 text-green-700"
        : "bg-red-100 text-red-700",
      className
    )}>
      {isOnline ? (
        <Wifi className="w-3 h-3" />
      ) : (
        <WifiOff className="w-3 h-3" />
      )}
      
      {pendingActions > 0 && (
        <span className="bg-current text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px]">
          {pendingActions > 9 ? '9+' : pendingActions}
        </span>
      )}
    </div>
  );
};